import { useCallback, useState } from "react";
import qualPApi from "../../../service/modules/qualP.api";
import clientsService from "../../../service/clients.service";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

require("firebase/auth");

export const cargoTypes = ["geral", "neogranel", "frigorificada"];
export const vehicleOptions = [
  { value: "HR", label: "HR", axles: 2 },
  { value: "3/4", label: "3/4", axles: 2 },
  { value: "TOCO", label: "Toco", axles: 2 },
  { value: "TRUCK", label: "Truck", axles: 3 },
  { value: "BI-TRUCK", label: "Bi-Truck", axles: 4 },
  { value: "CARRETA S", label: "Carreta S", axles: 5 },
  { value: "CARRETA LS", label: "Carreta LS", axles: 6 },
  { value: "VANDERLEIA", label: "Carreta Vanderleia", axles: 6 },
  { value: "BI-TREM", label: "Bitrem", axles: 9 },
  { value: "RODOTREM", label: "Rodotrem", axles: 9 },
];

export const eixoOptions = Array.from(
  new Set(vehicleOptions.map((option) => option.axles)),
);

export const cityOptions = [];

const initialForm = {
  origin: null,
  destinations: [null],
  optimizeRoute: true,
  cargoType: "",
  value: 0,
  weight: 0,
  vehicle: "",
  axles: "",
};

const defaultRates = {
  taxaAdm: 0.05,
  taxaGiro: 0.03,
  impostos: 0.12,
};

const vehicleRateByType = {
  Truck: 3.2,
};

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const percentFormatter = new Intl.NumberFormat("pt-BR", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const defaultRouteConfig = {
  calculateReturn: false,
  typeRoute: "efficient",
};

const defaultFuelConsumption = {
  fuelPrice: "6,19",
  kmFuel: "2,8",
};

const defaultTopSpeed = 80;

function getClientOptions(uidShipper) {
  return clientsService.getClientOptions(uidShipper);
}

function getVehicleRate(vehicleType) {
  const normalized = String(vehicleType || "").toLowerCase();

  if (normalized.includes("bitrem")) return 4.6;
  if (normalized.includes("carreta")) return 3.8;
  if (normalized.includes("truck")) return 3.2;
  if (normalized.includes("vuc")) return 2.4;
  if (normalized.includes("utilit")) return 2.1;

  return vehicleRateByType[vehicleType] || 3;
}

function getValidDestinations(destinations) {
  return (destinations || [])
    .filter(Boolean)
    .map((item) => String(item).trim());
}

function toPtDecimalString(value, fallback = "0,00") {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return fallback;
  return numericValue.toFixed(2).replace(".", ",");
}

function getComparableFormState(form) {
  const destinations = Array.isArray(form?.destinations)
    ? form.destinations.map((item) => String(item || "").trim())
    : [];

  const parsedValue = Number(form?.value);
  const parsedWeight = Number(form?.weight);

  return {
    origin: String(form?.origin || "").trim(),
    destinations,
    optimizeRoute: Boolean(form?.optimizeRoute),
    cargoType: String(form?.cargoType || "").trim(),
    value: Number.isFinite(parsedValue) ? parsedValue : 0,
    weight: Number.isFinite(parsedWeight) ? parsedWeight : 0,
    vehicle: String(form?.vehicle || "").trim(),
    axles: String(form?.axles || "").trim(),
  };
}

function getFormFingerprint(form) {
  return JSON.stringify(getComparableFormState(form));
}

export function buildSimulationPayload(form) {
  const validDestinations = getValidDestinations(form.destinations);
  const origin = String(form.origin || "").trim();
  const selectedVehicle = vehicleOptions.find(
    (option) => option.value === form.vehicle,
  );
  const selectedAxles = selectedVehicle?.axles ?? form.axles;
  const axisValue =
    String(selectedAxles || "") === "11" ? "all" : String(selectedAxles || "");
  const vehicleType = "truck";
  const vehicleRate = getVehicleRate(selectedVehicle?.label || form.vehicle);

  return {
    locations: origin ? [origin, ...validDestinations] : validDestinations,
    vehicle: {
      type: vehicleType,
      axis: axisValue,
      top_speed: defaultTopSpeed,
    },
    route: {
      optimized_route: Boolean(form.optimizeRoute),
      calculate_return: defaultRouteConfig.calculateReturn,
      type_route: defaultRouteConfig.typeRoute,
    },
    fuel_consumption: {
      fuel_price: defaultFuelConsumption.fuelPrice,
      km_fuel: defaultFuelConsumption.kmFuel,
    },
    simulation: {
      destinations: validDestinations,
      axles: axisValue,
      value: toPtDecimalString(form.value, "0,00"),
      weight: toPtDecimalString(form.weight, "0,00"),
      vehicle: vehicleType,
      vehicleRate,
      optimizeRoute: Boolean(form.optimizeRoute),
      cargoType: form.cargoType || "",
      rates: {
        taxaAdm: defaultRates.taxaAdm,
        taxaGiro: defaultRates.taxaGiro,
        impostos: defaultRates.impostos,
      },
    },
  };
}

function parseMaybePtNumber(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;

  const normalized = String(raw)
    .trim()
    .replace(/\./g, "")
    .replace(",", ".")
    .replace(/[^\d.-]/g, "");

  if (!normalized) return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function firstParsedNumber(...candidates) {
  for (const candidate of candidates) {
    const parsed = parseMaybePtNumber(candidate);
    if (parsed !== null) return parsed;
  }

  return null;
}

function getSimulationValidationError(form) {
  if (!form.origin) {
    return "Selecione a origem antes de calcular.";
  }

  if (!form.destinations.some(Boolean)) {
    return "Informe pelo menos um destino.";
  }

  return "";
}

function getEmptySimulationResult() {
  const { taxaAdm, taxaGiro, impostos } = defaultRates;

  return {
    precoANTT: 0,
    pedagio: 0,
    griss: 0,
    taxaAdm,
    taxaGiro,
    impostos,
    subtotal: 0,
    divisor: Math.max(0.01, 1 - impostos - taxaAdm - taxaGiro),
    total: 0,
  };
}

function mapApiResultToSimulationResult(apiData, fallbackResult) {
  const payload = apiData && typeof apiData === "object" ? apiData : {};
  const nested =
    payload?.data?.simulation && typeof payload.data.simulation === "object"
      ? payload.data.simulation
      : payload?.simulation && typeof payload.simulation === "object"
        ? payload.simulation
        : {};

  return {
    precoANTT: firstParsedNumber(
      nested.precoANTT,
      nested.preco_antt,
      fallbackResult.precoANTT,
    ),
    pedagio: firstParsedNumber(nested.pedagio, fallbackResult.pedagio),
    griss: firstParsedNumber(nested.griss, fallbackResult.griss),
    taxaAdm: firstParsedNumber(
      nested.taxaAdm,
      nested.taxa_adm,
      fallbackResult.taxaAdm,
    ),
    taxaGiro: firstParsedNumber(
      nested.taxaGiro,
      nested.taxa_giro,
      fallbackResult.taxaGiro,
    ),
    impostos: firstParsedNumber(nested.impostos, fallbackResult.impostos),
    subtotal: firstParsedNumber(nested.subtotal, fallbackResult.subtotal),
    divisor: firstParsedNumber(nested.divisor, fallbackResult.divisor),
    total: firstParsedNumber(
      nested.total,
      nested.valor_total,
      fallbackResult.total,
    ),
  };
}

export function useFreightSimulationActions(uidShipper) {
  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState(() => getEmptySimulationResult());
  const [formError, setFormError] = useState("");
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [lastPayload, setLastPayload] = useState(null);
  const [payload, setPayloadao] = useState([]);
  const [lastCalculatedFormFingerprint, setLastCalculatedFormFingerprint] =
    useState("");
  const [isRecalculateModalOpen, setIsRecalculateModalOpen] = useState(false);
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const currentFormFingerprint = getFormFingerprint(form);
  const hasSimulationChangesSinceLastCalculation = Boolean(
    hasCalculated &&
    lastCalculatedFormFingerprint &&
    currentFormFingerprint !== lastCalculatedFormFingerprint,
  );

  const handleClientOptions = useCallback(async (uidShipper) => {
    const snapshot = await getClientOptions(uidShipper);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  }, []);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCurrencyChange = (field) => (_event, numericValue) => {
    setForm((prev) => ({
      ...prev,
      [field]: Number.isFinite(numericValue) ? numericValue : 0,
    }));
  };

  const handleOriginChange = (_, value) => {
    setForm((prev) => ({
      ...prev,
      origin: value,
    }));
  };

  const handleOptimizeRouteChange = (_, checked) => {
    setForm((prev) => ({
      ...prev,
      optimizeRoute: checked,
    }));
  };

  const handleAddDestination = () => {
    setForm((prev) => ({
      ...prev,
      destinations: [...prev.destinations, null],
    }));
  };

  const handleRemoveDestination = (index) => {
    setForm((prev) => {
      if (prev.destinations.length === 1) return prev;

      return {
        ...prev,
        destinations: prev.destinations.filter((_, i) => i !== index),
      };
    });
  };

  const handleDestinationChange = (index, value) => {
    setForm((prev) => {
      const updated = [...prev.destinations];
      updated[index] = value;

      return {
        ...prev,
        destinations: updated,
      };
    });
  };

  const handleDragEnd = ({ source, destination }) => {
    if (!destination) return;
    if (source.index === destination.index) return;

    setForm((prev) => {
      const reordered = [...prev.destinations];
      const [movedItem] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, movedItem);

      return {
        ...prev,
        destinations: reordered,
      };
    });
  };

  const handleReset = () => {
    setForm(initialForm);
    setResult(getEmptySimulationResult());
    setFormError("");
    setHasCalculated(false);
    setLastPayload(null);
    setLastCalculatedFormFingerprint("");
    setIsRecalculateModalOpen(false);
  };

  const handleCalculate = async () => {
    if (isCalculating) return;
    if (!uidShipper) {
      setFormError("Usuário sem uidShipper vinculado.");
      return;
    }

    const validationError = getSimulationValidationError(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const payload = buildSimulationPayload(form);
    const calculatedFormFingerprint = getFormFingerprint(form);
    const emptyResult = getEmptySimulationResult();

    setLastPayload(payload);
    setIsCalculating(true);

    try {
      const response = await qualPApi.postQualP(payload, uidShipper);
      console.log("[FreightSimulation] Resposta da API de rota:", response);
      setPayloadao(response);

      const apiResult = mapApiResultToSimulationResult(
        response?.data,
        emptyResult,
      );

      setResult(apiResult);
      setFormError("");
      setHasCalculated(true);
      setLastCalculatedFormFingerprint(calculatedFormFingerprint);
      setIsRecalculateModalOpen(false);
    } catch (error) {
      console.error("[FreightSimulation] Erro ao calcular:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.details ||
        "";

      setResult(emptyResult);
      setFormError(
        backendMessage
          ? `${backendMessage}`
          : "Falha ao consultar a API de rota. Sem resposta da API, não foi possí­vel calcular.",
      );
      setHasCalculated(false);
      setLastCalculatedFormFingerprint("");
    } finally {
      setIsCalculating(false);
    }
  };

  const handleCreateFreight = async () => {
    if (hasSimulationChangesSinceLastCalculation) {
      setFormError(
        "A simulação está desatualizada. Refaça o cálculo antes de criar o frete.",
      );
      setIsRecalculateModalOpen(true);
      return;
    }

    if (!payload?.data?.data) {
      setFormError("Calcule a simulação antes de criar o frete.");
      return;
    }

    const stopPoints = [
      String(form.origin || "").trim(),
      ...getValidDestinations(form.destinations),
    ].filter(Boolean);

    try {
      const snapshot = await qualPApi.createFreightFromSimulation({
        uidShipper: user.uidShipper,
        ...payload.data.data,
        createUser: user.email,
        weightCargo: form.weight,
        typeVehicle: form.vehicle,
        stopPoints,
        user: { uid: user.uid, name: user.name },
        seller: user?.name || "",
        sellerId: user?.uid || "",
        value: form.value,
        axles: form.axles,
        cargoType: form.cargoType,
      });

      const createdFreightData = snapshot?.data?.data || {};

      navigate("/freightCopy", {
        state: {
          fromSimulation: true,
          simulationData: createdFreightData,
          stops:
            createdFreightData?.stopping_points ||
            createdFreightData?.stops ||
            [],
        },
      });
    } catch (error) {
      console.error("[FreightSimulation] Erro ao criar frete:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });

      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.details ||
        "";

      setFormError(
        backendMessage
          ? `Falha ao criar frete: ${backendMessage}`
          : "Falha ao criar frete a partir da simulação.",
      );
    }
  };


  
  const handleCloseRecalculateModal = () => {
    setIsRecalculateModalOpen(false);
  };

  const handleCloseFormErrorModal = () => {
    setFormError("");
  };

  const formatCurrency = (value) => currencyFormatter.format(value);
  const formatPercent = (value) => percentFormatter.format(value);

  return {
    form,
    result,
    formError,
    hasCalculated,
    hasSimulationChangesSinceLastCalculation,
    isRecalculateModalOpen,
    isCalculating,
    lastPayload,
    handleChange,
    handleCurrencyChange,
    handleOriginChange,
    handleOptimizeRouteChange,
    handleAddDestination,
    handleRemoveDestination,
    handleDestinationChange,
    handleDragEnd,
    handleReset,
    handleCalculate,
    handleCreateFreight,
    handleCloseRecalculateModal,
    handleCloseFormErrorModal,
    formatCurrency,
    formatPercent,
    handleClientOptions,
   
  };
}

